import React, { forwardRef, useImperativeHandle } from 'react';
import { RFQ } from '../../types';
import { useUploadStep } from '../../hooks/useUploadStep';
import UploadStepPanel from './upload/UploadStepPanel';
import { useLongWait } from '../../hooks/useLongWait';
import { useReportStageActivity } from '../../hooks/useStageActivity';
import { StageReport, failedStage } from '../../utils/stageActivity';
import { extractionCopy } from '../../utils/waitCopy';

/**
 * Wizard Step 1 — connected wrapper for the v2 Upload panel.
 * Keeps the imperative handle RFQWizard drives (submit on Continue, picker
 * open from Robbie's next-best-step) while logic lives in useUploadStep and
 * presentation in components/forms/upload/*.
 */

export interface UploadDocumentsHandle {
  submit: () => Promise<boolean>;
  isExtracting: boolean;
  openPicker: () => void;
}

interface UploadDocumentsProps {
  rfq: RFQ;
  onNext: () => void;
  onCancel: () => void;
  /** Tells the wizard Robbie is reading the files, in the overlay's words. */
  onWorking?: (stage: StageReport | null) => void;
}

const UploadDocuments = forwardRef<UploadDocumentsHandle, UploadDocumentsProps>(
  ({ rfq, onNext, onCancel, onWorking }, ref) => {
    const step = useUploadStep(rfq);
    const longWait = useLongWait(step.isExtracting, extractionCopy.longAfterMs(Math.max(step.readyNames.length, 1)));
    useReportStageActivity(
      onWorking,
      step.isExtracting
        ? 'bom-extraction'
        : step.extractionFailed ? failedStage('bom-extraction', extractionCopy.failedTitle) : null,
      step.isExtracting ? extractionCopy.panel(step.readyNames) : undefined,
    );

    useImperativeHandle(ref, () => ({
      submit: step.submit,
      isExtracting: step.isExtracting,
      openPicker: step.openPicker,
    }));

    return (
      <UploadStepPanel
        rows={step.rows}
        dropzone={step.dropzone}
        onRemove={step.removeFile}
        onBack={onCancel}
        onContinue={onNext}
        isExtracting={step.isExtracting}
        continueDisabled={step.isExtracting || step.hasUploading}
        extractionError={step.extractionError}
        pricing={step.pricing}
        extraction={step.extraction}
        readyNames={step.readyNames}
        longWait={longWait}
        extractionFailed={step.extractionFailed}
        onDismissFailure={step.dismissFailure}
      />
    );
  }
);

UploadDocuments.displayName = 'UploadDocuments';

export default UploadDocuments;
